import { Box, Stack, styled, Typography } from "@mui/material";
import { alpha } from "@mui/material";
import { StyledDayCellProps } from "../types/types";
import { DayNumberProps, StyledEventItemProps } from "../types/types";

export const CalendarHeader = styled(Stack)(({ theme }) => ({
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const WeekHeader = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        '& > *': {
            fontSize: '0.7rem',
            padding: theme.spacing(0.5),
        }
    }
}));

export const WeekDayCell = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 500,
    borderRight: `1px solid ${theme.palette.divider}`,
    textTransform: 'uppercase',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.5),
        fontSize: '0.7rem',
    }
}));

export const CalendarGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gridAutoRows: 'auto',
    width: '100%',
    gap: 0,
}));

export const StyledDayCell = styled(Box, {
    shouldForwardProp: (prop) => !['isCurrentMonth', 'isToday', 'isSelected'].includes(prop as string),
})<StyledDayCellProps>(({ theme, isCurrentMonth, isToday, isSelected }) => ({
    borderRight: `.2px solid ${theme.palette.divider}`,
    borderBottom: `.2px solid ${theme.palette.divider}`,
    borderLeft: `.2px solid ${theme.palette.divider}`,
    borderTop: `.2px solid ${theme.palette.divider}`,
    height: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    opacity: isCurrentMonth ? 1 : 0.5,
    transition: 'background-color 0.2s',
    minHeight: 120,
    [theme.breakpoints.down('md')]: {
        minHeight: 100,
    },
    [theme.breakpoints.down('sm')]: {
        minHeight: 80,
    }
}));

export const DayHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.25, 0.5),
    }
}));

export const DayNumber = styled(Typography, {
    shouldForwardProp: (prop) => !['isToday', 'isCurrentMonth'].includes(prop as string),
})<DayNumberProps>(({ theme, isToday, isCurrentMonth }) => ({
    fontSize: '0.875rem',
    backgroundColor: isToday ? theme.palette.primary.main : 'transparent',
    color: isToday
        ? theme.palette.primary.contrastText
        : isCurrentMonth
            ? theme.palette.text.primary
            : theme.palette.text.secondary,
    fontWeight: 400,
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    padding: 0,
    lineHeight: 1,
    [theme.breakpoints.down('sm')]: {
        fontSize: '0.75rem',
        width: '20px',
        height: '20px',
    }
}));

export const EventsContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0.5),
    paddingTop: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    flex: 1,
    // position: 'relative', 
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.25),
        gap: theme.spacing(0.25),
    }
}));

export const StyledEventItem = styled(Stack, {
    shouldForwardProp: (prop) => !['bgcolor', 'isEditing'].includes(prop as string),
})<StyledEventItemProps>(({ theme, bgcolor, isEditing = false }) => ({
    padding: theme.spacing(0.5, 1),
    backgroundColor: alpha(bgcolor, 0.7),
    color: theme.palette.text.primary,
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    cursor: isEditing ? 'text' : 'pointer',
    transition: 'all 0.2s',
    width: '100%',
    boxSizing: 'border-box',
    position: 'relative',
    '&:hover': {
        transform: isEditing ? 'none' : 'scale(1.02, 1.05) translateY(-1px)',
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: '0.7rem',
        padding: theme.spacing(0.25, 0.5),
    },
    ...(isEditing && {
        boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
        '&:focus-within': {
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
        }
    })
}));